import { useRef, useState, useEffect } from 'react';
import { X, ImagePlus, Trash2, CheckCircle, AlertCircle, RefreshCw, XCircle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { photoService, tagService } from '../../../services';
import { UploadProgress } from '../../../services/PhotoService';
import { addTag } from '../../../slice/tagsSlice';
import { RootState } from '../../../store/store';
import { createLogger } from '../../../utils/logger';
import TagPickerSection from '../../common/TagPickerSection';
import ConfirmDialog from '../../common/ConfirmDialog';

const logger = createLogger('UploadModal');

interface UploadModalProps {
  onClose: () => void;
  onUploadComplete: (photos: any[]) => void;
}

interface FileWithPreview {
  file: File;
  preview: string;
  visibility: 'public' | 'private';
}

interface FileUploadState extends UploadProgress {
  abortController?: AbortController;
}

export default function UploadModal({ onClose, onUploadComplete }: UploadModalProps) {
  const dispatch = useDispatch();
  const tags = useSelector((state: RootState) => state.tags);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadStates, setUploadStates] = useState<FileUploadState[]>([]);
  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const uploadedPhotosRef = useRef<any[]>([]);

   const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(e.target.files);
    }
  };

  const addFiles = (fileList: FileList) => {
    const newFiles: FileWithPreview[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      if (file.type.startsWith('image/')) {
        const preview = URL.createObjectURL(file);
        newFiles.push({
          file,
          preview,
          visibility: 'public',
        });
      }
    }
    setFiles([...files, ...newFiles]);
  };

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    URL.revokeObjectURL(files[index].preview);
    setFiles(newFiles);
  };

  const updateVisibility = (index: number, visibility: 'public' | 'private') => {
    const newFiles = [...files];
    newFiles[index].visibility = visibility;
    setFiles(newFiles);
  };

  const handleTagAdd = (tagId: number) => {
    if (!selectedTagIds.includes(tagId)) {
      setSelectedTagIds([...selectedTagIds, tagId]);
    }
  };

  const handleTagRemove = (tagId: number) => {
    setSelectedTagIds(selectedTagIds.filter(id => id !== tagId));
  };

  const handleCreateAndAddTag = async (name: string, type: 'person' | 'place' | 'year' | 'event' | 'custom') => {
    try {
      const newTag = await tagService.createTag(name, type);
      dispatch(addTag(newTag));
      handleTagAdd(newTag.id);
    } catch (err) {
      logger.error('Failed to create tag:', err);
    }
  };

  const handleUploadClick = async () => {
    if (files.length === 0) return;

    // If tags are selected, show confirm dialog first
    if (selectedTagIds.length > 0) {
      setShowConfirmDialog(true);
    } else {
      // No tags, proceed directly
      await performUpload();
    }
  };

  const performUpload = async () => {
    if (files.length === 0) return;

    try {
      setLoading(true);
      setError(null);
      setShowConfirmDialog(false);
      uploadedPhotosRef.current = [];

      const fileList = files.map((fileWithPreview) => fileWithPreview.file);
      
      // Create abort controller for this upload session
      uploadAbortControllerRef.current = new AbortController();

      // Use new per-file upload with progress tracking
      const uploadedPhotos = await photoService.uploadPhotosWithProgress(
        fileList,
        selectedTagIds.length > 0 ? selectedTagIds : undefined,
        {
          onProgress: (states) => {
            setUploadStates(states);
          },
          onComplete: (photos) => {
            uploadedPhotosRef.current = photos;
            logger.info(`Upload completed: ${photos.length} photos`);
          },
        },
        uploadAbortControllerRef.current.signal
      );

      logger.info(`Uploaded ${uploadedPhotos.length} photos${selectedTagIds.length > 0 ? ` with ${selectedTagIds.length} tag(s)` : ''}`);
      onUploadComplete(uploadedPhotos);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setError(message);
      logger.error('Upload error:', err);
    } finally {
      setLoading(false);
      uploadAbortControllerRef.current = null;
    }
  };

  const handleCancelUpload = () => {
    if (uploadAbortControllerRef.current) {
      uploadAbortControllerRef.current.abort();
      logger.info('Upload cancelled by user');
    }
  };

  const handleRetryFile = async (fileIndex: number) => {
    if (!uploadStates[fileIndex] || uploadStates[fileIndex].status !== 'error') return;
    
    const file = files[fileIndex].file;
    try {
      uploadAbortControllerRef.current = new AbortController();
      
      const formData = new FormData();
      formData.append('files', file);
      if (selectedTagIds.length > 0) {
        formData.append('tagIds', JSON.stringify(selectedTagIds));
      }

      const response = await photoService['uploadPhotos'](
        [file],
        selectedTagIds.length > 0 ? selectedTagIds : undefined
      );

      if (response && response.length > 0) {
        uploadedPhotosRef.current.push(response[0]);
        const newStates = [...uploadStates];
        newStates[fileIndex] = {
          ...newStates[fileIndex],
          status: 'done',
          progress: 100,
          error: undefined,
        };
        setUploadStates(newStates);
        logger.info(`Retried successfully: ${file.name}`);
      }
    } catch (err) {
      logger.error(`Retry failed for ${file.name}:`, err);
    }
  };

  // Get available tags for the picker
  const allTags = tags.allIds.map(id => tags.byId[id]).filter(Boolean);

  // Overall upload progress (lives outside the file list panel)
  const totalFiles = files.length;
  const completedCount = uploadStates.filter(s => s.status === 'done').length;
  const erroredCount = uploadStates.filter(s => s.status === 'error').length;
  const cancelledCount = uploadStates.filter(s => s.status === 'cancelled').length;
  const processedCount = completedCount + erroredCount + cancelledCount;
  const uploadingIndex = uploadStates.findIndex(s => s.status === 'uploading');
  const currentFileNumber = uploadingIndex >= 0
    ? uploadingIndex + 1
    : Math.min(processedCount, totalFiles);
  const overallPercent = totalFiles > 0
    ? Math.round(uploadStates.reduce((sum, s) => sum + (s?.progress || 0), 0) / totalFiles)
    : 0;
  const isUploadDone = processedCount === totalFiles && totalFiles > 0;

  return (
    <div className="upload-modal-overlay" onClick={onClose}>
      <div
        className="upload-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="upload-modal-header">
          <h2 className="upload-modal-title">Upload Photos</h2>
          <button
            onClick={onClose}
            className="upload-modal-close-btn"
            aria-label="Close upload"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Drop zone */}
        <div
          ref={dropZoneRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`upload-drop-zone ${dragOver ? 'drag-over' : ''}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
          <div className="upload-drop-zone-icon">
            <ImagePlus size={32} strokeWidth={1.5} />
          </div>
          <div className="upload-drop-zone-text">Drag & drop photos here or click to select</div>
          <div className="upload-drop-zone-subtext">JPG, PNG, WebP (up to 50 MB each)</div>
        </div>

        {/* Tag Picker */}
        <TagPickerSection
          selectedTagIds={selectedTagIds}
          availableTags={allTags}
          onTagAdd={handleTagAdd}
          onTagRemove={handleTagRemove}
          onCreateTag={handleCreateAndAddTag}
          isLoading={loading}
        />

        {/* Overall upload progress (outside the file list panel) */}
        {loading && totalFiles > 0 && (
          <div className={`upload-overall-progress ${isUploadDone ? 'done' : ''} ${erroredCount > 0 ? 'has-errors' : ''}`}>
            <div className="upload-overall-progress-label">
              {isUploadDone
                ? `Uploaded ${completedCount} of ${totalFiles} photos${erroredCount > 0 ? ` (${erroredCount} failed)` : ''}`
                : `Uploading ${currentFileNumber} of ${totalFiles} photos`}
            </div>
            <div className="upload-overall-progress-bar-container">
              <div
                className="upload-overall-progress-bar-fill"
                style={{ width: `${overallPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* File list */}
        {files.length > 0 && (
          <div className="upload-file-list">
            {files.map((fileWithPreview, index) => {
              const uploadState = uploadStates[index];
              const isUploading = uploadState?.status === 'uploading';
              const isDone = uploadState?.status === 'done';
              const hasError = uploadState?.status === 'error';
              const isCancelled = uploadState?.status === 'cancelled';

              return (
                <div key={index} className={`upload-file-item ${isUploading ? 'uploading' : ''} ${isDone ? 'done' : ''} ${hasError ? 'error' : ''}`}>
                  <img
                    src={fileWithPreview.preview}
                    alt={`Preview ${index}`}
                    className="upload-file-preview"
                  />
                  <div className="upload-file-info">
                    <div className="upload-file-name">{fileWithPreview.file.name}</div>
                    <div className="upload-file-size">
                      {(fileWithPreview.file.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                    
                    {/* Progress bar (shown during upload) */}
                    {loading && (
                      <>
                        <div className="upload-progress-bar-container">
                          <div 
                            className="upload-progress-bar-fill" 
                            style={{ width: `${uploadState?.progress || 0}%` }}
                          />
                        </div>
                        <div className="upload-file-status">
                          {isUploading && (
                            <>
                              <div className="upload-status-spinner" />
                              <span>Uploading... {uploadState?.progress}%</span>
                            </>
                          )}
                          {isDone && (
                            <>
                              <CheckCircle size={16} />
                              <span>Complete</span>
                            </>
                          )}
                          {hasError && (
                            <>
                              <AlertCircle size={16} />
                              <span>{uploadState?.error || 'Failed'}</span>
                            </>
                          )}
                          {isCancelled && (
                            <>
                              <XCircle size={16} />
                              <span>Cancelled</span>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Action buttons */}
                  {loading ? (
                    <div className="upload-file-actions-during">
                      {hasError && (
                        <button
                          onClick={() => handleRetryFile(index)}
                          className="upload-action-btn retry"
                          title="Retry upload"
                          aria-label="Retry upload"
                        >
                          <RefreshCw size={16} strokeWidth={1.5} />
                        </button>
                      )}
                      {isUploading && (
                        <button
                          onClick={handleCancelUpload}
                          className="upload-action-btn cancel"
                          title="Cancel upload"
                          aria-label="Cancel upload"
                        >
                          <XCircle size={16} strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="upload-visibility-toggle">
                        <button
                          className={`upload-visibility-btn ${fileWithPreview.visibility === 'public' ? 'active' : ''}`}
                          onClick={() => updateVisibility(index, 'public')}
                        >
                          Public
                        </button>
                        <button
                          className={`upload-visibility-btn ${fileWithPreview.visibility === 'private' ? 'active' : ''}`}
                          onClick={() => updateVisibility(index, 'private')}
                        >
                          Private
                        </button>
                      </div>
                      <button
                        onClick={() => removeFile(index)}
                        className="upload-remove-btn"
                        title="Remove file"
                        aria-label="Remove file"
                      >
                        <Trash2 size={18} strokeWidth={1.5} />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Error message */}
        {error && <div className="error">{error}</div>}

        {/* Actions */}
        <div className="upload-actions">
          <button
            onClick={onClose}
            disabled={loading}
            className="upload-btn-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleUploadClick}
            disabled={loading || files.length === 0}
            className="upload-btn-submit"
          >
            {loading ? 'Uploading...' : 'Upload All'}
          </button>
        </div>

        {/* Confirm Dialog (only shown when tags are selected) */}
        <ConfirmDialog
          isOpen={showConfirmDialog}
          title="Apply Tags to All Photos?"
          message={`Apply ${selectedTagIds.length} tag(s) to ${files.length} photo(s)?`}
          confirmLabel="Apply & Upload"
          cancelLabel="Cancel"
          isLoading={loading}
          onConfirm={performUpload}
          onCancel={() => setShowConfirmDialog(false)}
        />
      </div>
    </div>
  );
}
